import { useState, useMemo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';

import { PropertySelector } from './Components/PropertySelector';
import { IncidentReportListTable } from './Components/IncidentReportListTable';
import { GenerateIncidentInvitationModal } from './Components/GenerateIncidentInvitationModal';
import { GenerateIncidentReportModal } from './Components/GenerateIncidentReportModal';
import { DeleteConfirmationModal } from './Components/DeleteConfirmationModal';

import {
  defaultIncidentReport,
  getIncidentReportInitialValues,
} from './Components/FormikHelpers';

import { getPropertyList } from '../../../Inventory/pages/FacilityInventory/Helpers/Api';
import {
  createIncidentReport,
  getIncidentReports,
  updateIncidentReport,
  deleteIncidentReport,
  deleteInvitation,
  getMyInvitations,
  getIncidentStatusCounts,
} from './Components/FormikHelpers/Api';
import { useFormik } from 'formik';
import { isNotificationComplete, isInvestigationComplete } from './Components/GenerateIncidentReportModal';
import { setLoading } from '../../../../store/actions';

const getCurrentUserInfo = (user) => {
  if (!user) return { name: '', managerphoneNo: '', email: '' };
  const first = user.first_name || user.firstname || user.name || '';
  const last = user.last_name || user.lastname || '';
  const name = `${first} ${last}`.trim() || 'Unknown User';
  const managerphoneNo = user.phoneNo || user.phone || user.mobile || user.contact || '';
  const email = user.email || '';
  return { name, managerphoneNo, email };
};

const IncidentReport = () => {
  const dispatch = useDispatch();

  const [properties, setProperties] = useState([]);
  const [reports, setReports] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [mode, setMode] = useState('reports');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [initialComplete, setInitialComplete] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});

  const [selectedFilters, setSelectedFilters] = useState({
    propertyId: '',
    buildingName: '',
    status: 'all',
    search: '',
    startDate: '',
    endDate: '',
  });

  const formik = useFormik({
    initialValues: defaultIncidentReport,
    enableReinitialize: true,
  });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;

    setUser(currentUser);
    dispatch(setLoading(true));

    try {
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch properties', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const fetchReports = useCallback(async (filters) => {
    dispatch(setLoading(true));

    try {
      const response = await getIncidentReports({
        propertyId: filters.propertyId || undefined,
        buildingName: filters.buildingName || undefined,
        status: filters.status === 'all' ? undefined : filters.status,
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      setReports(response?.data || response || []);
    } catch (err) {
      console.error(err);
      setReports([]);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const fetchStatusCounts = useCallback(async (filters) => {
    try {
      const response = await getIncidentStatusCounts({
        propertyId: filters.propertyId || undefined,
        buildingName: filters.buildingName || undefined,
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      console.log(response); // logs now appear as user confirmed
      setStatusCounts(response?.data || {});
    } catch (err) {
      console.error('Failed to fetch status counts', err);
      setStatusCounts({});
    }
  }, []);

  const fetchInvitations = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const response = await getMyInvitations();
      setInvitations(response?.data || response || []);
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const filteredInvitations = useMemo(() => {
    if (mode !== 'invitations') return [];
    return invitations.filter((i) => {
      if (selectedFilters.propertyId && i.property?.propertyId?.toString() !== selectedFilters.propertyId) return false;
      if (selectedFilters.buildingName && i.property?.buildingName !== selectedFilters.buildingName) return false;
      if (selectedFilters.status !== 'all') {
        if (selectedFilters.status === 'open' && (i.status === 'pending' || i.status === 'sent')) return true;
        if (selectedFilters.status === 'resolved' && i.status === 'used') return true;
        return false;
      }
      return true;
    });
  }, [invitations, mode, selectedFilters]);

  const tableItems = mode === 'reports' ? reports : filteredInvitations;

  const refresh = useCallback(() => {
    if (mode === 'reports') {
      fetchReports(selectedFilters);
    } else {
      fetchInvitations();
    }
  }, [mode, selectedFilters, fetchReports, fetchInvitations]);

  const refreshStatusCounts = useCallback(() => {
    if (mode === 'reports') {
      fetchStatusCounts(selectedFilters);
    }
  }, [mode, selectedFilters, fetchStatusCounts]);

  const submitReport = async (values) => {
    dispatch(setLoading(true));
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

      if (selectedItem?._id) {
        await updateIncidentReport(selectedItem._id, values);
        return selectedItem._id;
      }

      const response = await createIncidentReport({ ...values, timezone });
      return response._id;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleAddReportClick = () => {
    setSelectedItem(null);
    setInitialComplete(false);

    const userInfo = getCurrentUserInfo(user);

    formik.resetForm({
      values: {
        ...defaultIncidentReport,
        propertyDetails: {
          propertyId: selectedFilters.propertyId || '',
          buildingName: selectedFilters.buildingName || '',
        },
        notification: {
          ...defaultIncidentReport.notification,
          managerReporting: userInfo.name,
          managerEmail: userInfo.email,
          managerphoneNo: userInfo.managerphoneNo,
          personInvolved: defaultIncidentReport.notification.personInvolved || [],
        },
      },
    });
    setIsReportModalOpen(true);
  };

  const handleEditReportClick = async (report) => {
    setSelectedItem(report);
    setIsAutoFilling(true);
    dispatch(setLoading(true));

    try {
      const values = await getIncidentReportInitialValues(report._id);
      formik.setValues(values);

      const notificationComplete = isNotificationComplete(values);
      const investigationComplete = isInvestigationComplete(values);
      setInitialComplete(notificationComplete && investigationComplete);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to load incident report details for editing', 'error');
    } finally {
      setIsAutoFilling(false);
      dispatch(setLoading(false));
      setIsReportModalOpen(true);
    }
  };

  const handleViewOrEditInvitation = (inv) => {
    setSelectedInvitation(inv);
    setIsInvitationModalOpen(true);
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem?._id) return;

    dispatch(setLoading(true));

    try {
      if (mode === 'reports') {
        await deleteIncidentReport(selectedItem._id);
      } else {
        await deleteInvitation(selectedItem._id);
      }

      setIsDeleteModalOpen(false);
      setSelectedItem(null);
      refresh();
      refreshStatusCounts();

      Swal.fire({
        title: 'Deleted!',
        text: `${mode === 'reports' ? 'Incident report' : 'Invitation'} has been deleted.`,
        icon: 'success',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire('Error', `Failed to delete ${mode === 'reports' ? 'incident report' : 'invitation'}`, 'error');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleInvitationSuccess = () => {
    setIsInvitationModalOpen(false);
    setSelectedInvitation(null);
    if (mode === 'invitations') refresh();
  };

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    if (mode === 'reports') {
      fetchReports(selectedFilters);
      refreshStatusCounts();
    }
  }, [selectedFilters, mode, fetchReports, refreshStatusCounts]);

  useEffect(() => {
    if (mode === 'invitations') {
      fetchInvitations();
    }
  }, [mode, fetchInvitations]);

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Incident Reports</title>
      </MetaTags>

      <div className="container-fluid">
        <Breadcrumbs title="Maintenance" breadcrumbItem="Incident Reports" />

        <PropertySelector
          propertyList={properties}
          selectedFilters={selectedFilters}
          onPropertySelectionChange={setSelectedFilters}
          onAddClick={handleAddReportClick}
          isLoading={false}
          mode={mode}
          onModeChange={setMode}
          onOpenInvitationModal={() => handleViewOrEditInvitation(null)}
          statusCounts={statusCounts}
        />

        <IncidentReportListTable
          items={tableItems}
          onDelete={handleDelete}
          onEdit={mode === 'reports' ? handleEditReportClick : handleViewOrEditInvitation}
          userRole={user?.role || 'other'}
          mode={mode}
          onRefresh={refresh}
        />

        <GenerateIncidentReportModal
          formik={formik}
          filters={selectedFilters}
          properties={properties}
          isModalOpen={isReportModalOpen}
          setIsModalOpen={setIsReportModalOpen}
          isEdit={!!selectedItem}
          onRefresh={refresh}
          submitReport={submitReport}
          userRole={user?.role || 'other'}
          initialComplete={initialComplete}
        />

        <GenerateIncidentInvitationModal
          isOpen={isInvitationModalOpen}
          toggle={() => {
            setIsInvitationModalOpen(false);
            setSelectedInvitation(null);
          }}
          initialData={selectedInvitation}
          propertyId={selectedFilters.propertyId}
          buildingName={selectedFilters.buildingName}
          propertyTitle={properties.find((p) => p._id === selectedFilters.propertyId)?.title || ''}
          onSuccess={handleInvitationSuccess}
        />

        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedItem(null);
          }}
          onDelete={handleDeleteConfirm}
          report={selectedItem}
          mode={mode}
        />
      </div>
    </div>
  );
};

export { IncidentReport };